import { type ModalLayer, toLayerParamKey } from 'src/navigation/reservedParams'
import {
  billPayDetailUrl,
  deactivateBillPayCardUrl,
  deactivateVirtualCardUrl,
  virtualCardDetailUrl,
} from 'src/navigation/urls'

// Overlay URL-route registry (EX-39749 / EX-39786).
//
// The single home for "a dedicated, pretty URL that opens an overlay" — mapping
// a path like `/virtualcards/:id/deactivate/` to its query-param overlay form
// `/virtualcards/:id?modal=deactivate&modalId=:id`. This is how we keep human-
// readable, shareable, public-facing URLs for modals/flyouts while the overlay
// itself is rendered from query params (see `modalParams.ts`); it also preserves
// any pre-existing deep link / `<Lnk>` trigger that still points at the dedicated
// URL. `OverlayRedirects` renders one redirect `<Route>` per entry, and
// `__tests__/overlayRoutes.test.tsx` asserts these paths never collide with the
// flat page route registry.
//
// Data only (no react-router) so the collision test and any tooling can import
// it without pulling routing in; the redirect rendering lives in
// `OverlayRedirects.tsx`.

export type OverlayRoute = {
  // The dedicated overlay URL as a react-router path pattern, e.g.
  // `/virtualcards/:id/deactivate/`. This is the pretty/shareable route that
  // opens the overlay.
  path: string
  // The query-param overlay it maps to: layer (`modal`/`flyout`) + identity.
  layer: ModalLayer
  name: string
  // The real page the overlay renders on top of, built from the matched route
  // params, e.g. `({ id }) => /virtualcards/:id`.
  toPathname: (params: Record<string, string>) => string
  // Maps a matched route param to the overlay's data sub-key. `{ id: 'id' }`
  // turns the route `:id` into the layer's `<layer>Id` param (`modalId`).
  paramMap?: Record<string, string>
}

export const deactivateVirtualCardOverlayRoute: OverlayRoute = {
  path: deactivateVirtualCardUrl(':id'),
  layer: 'modal',
  name: 'deactivate',
  toPathname: ({ id }) => virtualCardDetailUrl(id),
  paramMap: { id: 'id' },
}

export const deactivateBillPayCardOverlayRoute: OverlayRoute = {
  path: deactivateBillPayCardUrl(':id'),
  layer: 'modal',
  name: 'deactivate',
  toPathname: ({ id }) => billPayDetailUrl(id),
  paramMap: { id: 'id' },
}

export const OVERLAY_ROUTES: OverlayRoute[] = [
  deactivateVirtualCardOverlayRoute,
  deactivateBillPayCardOverlayRoute,
]

/**
 * Build the `{ pathname, search }` an overlay URL route resolves to: the real
 * page (`toPathname`) plus the reserved overlay params
 * (`?<layer>=<name>&<layer><Key>=<value>`). Param keys go through
 * `toLayerParamKey` so the mapping can never drift from the routing hooks.
 */
export const overlayRedirectLocation = (
  route: OverlayRoute,
  params: Record<string, string>,
): { pathname: string; search: string } => {
  const search = new URLSearchParams()
  search.set(route.layer, route.name)
  Object.entries(route.paramMap ?? {}).forEach(([routeKey, subKey]) => {
    const value = params[routeKey]
    if (value !== undefined) {
      search.set(toLayerParamKey(route.layer, subKey), value)
    }
  })
  return { pathname: route.toPathname(params), search: `?${search.toString()}` }
}

export const deactivateOverlayLocation = (
  id: string,
  isBillPay: boolean,
): { pathname: string; search: string } =>
  overlayRedirectLocation(
    isBillPay
      ? deactivateBillPayCardOverlayRoute
      : deactivateVirtualCardOverlayRoute,
    { id },
  )
